import axios from 'axios';
import Qs from 'qs';
import { getDeviceName, getSystemName, isMac, isWindows } from '@/utils/device';
import { ElMessage, ElMessageBox } from 'element-plus';
import sensorsTrackJM from '@/common/sensorsTrackJM';
import { decrypt, isOverseasEnv } from '@/utils/api';
import { decryptRequest } from '@/utils/decryptRequest';
import { safeJSONParse } from '@/utils/bigNumberUtils';
import { getStorageAuthInfo, storage, checkLogin } from '@/utils/storageOperateApi';
import { LOCAL_STORAGE_KEY, LOGIN_INVALID_CODES } from '@/constants';
import { getCookie } from '@/utils/cookie';

import store from '@/store';
import { isBuild, COMEINURL, CLIENT_URL, PORTVERSION } from './host';
import { txCaptcha } from '@/utils/captcha';
import router from '@/router';
import { isElectron } from '@/utils/electron';
import { resolveOsHeader } from '@/utils/embedEntry';
import { startWorkBuddySessionRecovery } from '@/utils/workBuddySessionRecovery';
import { isAuthTransitioning } from '@/utils/authTransition';
import { trackGlobalAlert, ALERT_CHANNEL, ALERT_SCENE } from '@/utils/globalAlertTrack';
import { debounce } from 'lodash-es';
import i18n from '@/plugins/i18n';
import slsLogger, { generateGUID } from '@/utils/slsLogger';
import { getOverseaHeader, getRedirectUrl } from '@/utils/oversea';
import { nbiConfirmDialog } from '@/common/hooks/useMember';

/** 加密的盐 */
let requestSalt = isBuild ? '039ed7d839d8915bf01e4f49825fcc6b' : '4bdc587e345b45a3be4a57dc95637822';
if (isElectron) {
  requestSalt = isBuild ? 'd024485429d943febe86fc44f5dd87a1' : 'faf747425c17458db603e119c7429b17';
}

export const REQUESTSALT = requestSalt;

axios.defaults.timeout = 30000;
axios.defaults.retry = 0;
axios.defaults.retryDelay = 1000;
axios.defaults.retryCount = 0;

// 用于处理大数字精度的响应转换器
export const bigNumberTransformResponse = [
  function (data, headers) {
    if (headers && headers['content-type'] && headers['content-type'].includes('application/json')) {
      if (typeof data === 'string') {
        return safeJSONParse(data);
      }
    }
    return data;
  }
];

let isLoginOut = false;
/** ERR_NETWORK 全局只弹一次，恢复连通后清除 */
let networkErrorToastShown = false;
let bolCaptcha = false;
let showSecurityCaptcha = false;
let requestQueue = [];

const MCP_OAUTH_AUTHORIZE_PATH = '/mcp/oauth/authorize';
const MCP_OAUTH_LOGIN_PATH = '/mcp/oauth/login';
const THIRD_PARTY_OAUTH_AUTHORIZE_PATH = '/workbuddy/oauth/authorize';
const THIRD_PARTY_OAUTH_LOGIN_PATH = '/workbuddy/oauth/login';
// MCP OAuth 授权页（Base 副本，走主站网关）：与上面两组同理，登录态失效需回自己的独立登录页
const MCP_OAUTH_BASE_AUTHORIZE_PATH = '/open/oauth/authorize';
const MCP_OAUTH_BASE_LOGIN_PATH = '/open/oauth/login';
/**
 * 可由 WorkBuddy 静默恢复接管的登录失效码。
 * 不含 401：该码只提示无权限、不退出登录，本就不需要重建登录态。
 */
const SESSION_RECOVERABLE_CODES = new Set(['201', '20101', '20102', '403']);
const LOGIN_INVALID_MESSAGE_KEYS = {
  '该账号已在其他设备登录，为保证账号安全，请重新登录': 'common.tip.account_logged_in_elsewhere',
  '您的账号登录状态已失效，请重新登录': 'common.tip.login_expired',
  '为保护账号安全，请重新登录': 'common.tip.login_required'
};

const getLoginInvalidMessage = (response) => {
  const messageKey = LOGIN_INVALID_MESSAGE_KEYS[response.errmsg];
  return messageKey
    ? i18n.global.t(messageKey)
    : response?.errmsg || response?.errordesc || i18n.global.t('common.tip.login_expired');
};

/**
 * 本次错误处理的弹框上下文（触发场景与请求配置）。
 * 由 Axios.prototype.error 在同一条同步调用链上写入、alertFunc 立即消费，不存在并发覆盖。
 */
let alertContext = {};

/**
 * 取出并清空弹框上下文，避免上一次未弹框的上下文残留到下一次上报。
 * @returns {{ scene?: string, config?: object }} 本次上下文，未设置时为空对象
 */
function consumeAlertContext() {
  const context = alertContext;
  alertContext = {};
  return context;
}

const alertFunc = function (
  code,
  message,
  bolRedirect = true,
  confirmText = i18n.global.t('common.button.confirm'),
  callback
) {
  const context = consumeAlertContext();
  if (isLoginOut || ([201, 403].includes(code) && store.state.isLogin)) return;
  // 本机正在重新登录换发鉴权：本地 token 处于空窗期，此刻的失效码是假象，弹框会把用户又赶回登录页一次
  if (LOGIN_INVALID_CODES.has(String(code)) && isAuthTransitioning()) return;
  // WorkBuddy 会话能凭 wb.login 静默重建登录态，不必弹框把用户赶回登录页
  if (SESSION_RECOVERABLE_CODES.has(String(code)) && startWorkBuddySessionRecovery()) return;
  isLoginOut = true;
  // 这个框会把用户赶回登录页，上报触发链路与场景，便于线上还原「为什么又让我重新登录」
  trackGlobalAlert({ channel: ALERT_CHANNEL.MAIN, code, message, ...context });
  ElMessageBox.alert(message, i18n.global.t('common.tip.tips'), {
    customClass: 'w480 message-box-normal',
    confirmButtonText: confirmText,
    callback: () => {
      isLoginOut = false;
      if (callback) return callback();
      if (window.location.pathname !== '/login' && bolRedirect) {
        const redirectUrl = getRedirectUrl();
        if (window.location.pathname === redirectUrl) {
          location.href = redirectUrl;
          return;
        }
        router.push(redirectUrl);
      }
    }
  });
};

// 通用错误节流
const debounceError = debounce((response) => {
  const msg = response.msg || response.errordesc || response.errmsg;
  msg &&
    ElMessage({
      showClose: true,
      message: msg,
      type: 'error'
    });
}, 500);

// 请求拦截器
axios.interceptors.request.use((config) => {
  // 记录请求开始时间
  config.metaData = { startTime: Date.now() };
  //默认设置token到请求头
  const userInfo = getStorageAuthInfo(LOCAL_STORAGE_KEY.JM_AUTH_INFO) || {};
  const headers = config.headers?.act ? config.headers : config.params;
  try {
    const act = headers?.act || config?.data?.act || '';
    // 分享解析页免鉴权，避免未登录预览时携带用户态触发登录过期弹窗。
    if (
      userInfo &&
      ![
        'weblogin',
        'query-user-org',
        'change-user-identity',
        'session-share-detail',
        'share-resolve',
        'node-share-download'
      ].includes(act)
    ) {
      // 用户ID (非必填)
      // 获取机构时不需要携带uid
      // 优先使用调用方显式传入的 uid（如登录/切号时携带本次用户的 uid），
      // 仅在未显式传入时回退到 storage 中的 uid，避免旧用户 uid 覆盖新用户造成 uid 与 token 串号
      config.headers.uid = config.headers.uid || (userInfo ? userInfo.uid : '');
      // 会话令牌 (非必填)
      if (config.headers.hasOwnProperty('token') || config.headers.hasOwnProperty('webtoken')) {
        config.headers.token = config.headers.web_token = config.headers.token || userInfo.webtoken;
      } else {
        config.headers.token = config.headers.web_token = userInfo ? userInfo.webtoken || userInfo?.token || '' : '';
      }
      // 海外环境请求头，所有方法统一在此添加一次
      Object.assign(config.headers, getOverseaHeader(true));
    }
    if (act === 'change-user-identity') {
      config.headers.uid = '';
    }
    const release = getCookie('release');
    if (release) {
      config.headers.release = release;
    }
    const symbol = getCookie('symbol');
    if (symbol) {
      config.headers.symbol = symbol;
    }
    // 品牌渠道 动态判断 mac 或 windows
    config.headers.brandChannel = isMac ? 'mac' : isWindows ? 'windows' : '';

    if (window.comeinChannel) {
      config.headers.sourcePlatform = window.comeinChannel;
    }
  } catch (e) {
    console.log('请求拦截器错误', e);
  }

  // 接口是否上报时间耗时
  if (config?.needTime) {
    const requestId = generateGUID();
    config.__requestId = requestId;

    const startEvent = new CustomEvent('httpLoadStart', {
      detail: {
        requestId,
        url: config.url,
        ...config,
        startTime: Date.now()
      }
    });
    window.dispatchEvent(startEvent);
  }

  return config;
});

const resInterceptors = axios.interceptors.response.use((response) => {
  // 记录请求结束时间
  response.config.metaData.endTime = new Date();
  // 计算请求响应耗时
  response.costTime = response.config.metaData.endTime - response.config.metaData.startTime;
  handleResponseTrack(response);
  return response;
}, resRetryInterceptor);

function resRetryInterceptor(err) {
  const config = err.config;
  if (!config) return Promise.reject(err);

  err.config.metaData.endTime = new Date();
  err.costTime = err.config.metaData.endTime - err.config.metaData.startTime;
  handleResponseTrack(err);

  // If config does not exist or the retry option is not set, reject
  if (!config.retry) return Promise.reject(err);

  // Check if we've maxed out the total number of retries
  if (config.retryCount >= config.retry) {
    // Reject with the error
    axios.interceptors.response.eject(resInterceptors);
    return Promise.reject(err);
  }

  // Increase the retry count
  config.retryCount += 1;
  // Create new promise to handle exponential backoff
  const backoff = new Promise(function (resolve) {
    setTimeout(function () {
      resolve();
    }, config.retryDelay || 1);
  });

  // Return the promise in which recalls axios to retry the request
  return backoff.then(function () {
    return axios.request(config);
  });
}

/**
 * 处理接口响应埋点
 * @param {*} res axios请求拦截器参数
 * @returns
 */
export function handleResponseTrack(res) {
  const { needTrack, trackParams = {} } = res?.config || {};
  if (!needTrack) return;
  const { statisticnum = '', ...extra } = trackParams;
  if (!statisticnum) return;
  const extraParams = {
    ...extra,
    costTime: res.costTime || 0 // 请求响应耗时
  };
  sensorsTrackJM({
    statisticnum,
    extra: JSON.stringify(extraParams)
  });
}

function getHeaders(res) {
  const headers = res.request.getAllResponseHeaders();
  const arr = headers.trim().split(/[\r\n]+/);
  const headerMap = {};
  arr.forEach(function (line) {
    const parts = line.split(': ');
    const header = parts.shift();
    headerMap[header] = parts.join(': ');
  });
  return headerMap;
}

function Axios() {
  // this.isLoginOut = false;
  // this.requestQueue = [];
}

let retryTokenCount = 0;
Axios.prototype.success = function (res) {
  networkErrorToastShown = false;
  res.config = res.config || {};
  if (res.config?.needTime) {
    const endEvent = new CustomEvent('httpLoadEnd', {
      detail: {
        ...res.config,
        requestId: res.config.__requestId,
        url: res.config.url,
        endTime: Date.now(),
        response: res.data,
        status: res.status,
        responseHeaders: JSON.stringify(res.request.getAllResponseHeaders())
      }
    });
    window.dispatchEvent(endEvent);
  } else if (!res.config?.hideLogger) {
    slsLogger.xhr({
      ...res.config,
      response: res.data,
      status: res.status,
      responseHeaders: JSON.stringify(res.request.getAllResponseHeaders())
    });
  }

  retryTokenCount = 0;
  const headerMap = getHeaders(res);
  if (headerMap.k) {
    try {
      res.data = JSON.parse(decryptRequest(headerMap.k, res.data, REQUESTSALT));
    } catch (err) {
      slsLogger.xhr({
        ...err.config,
        api: err.config.url,
        response: err.data,
        status: err.status,
        responseHeaders: JSON.stringify(err.request.getAllResponseHeaders()),
        message: 'api解析失败: ' + REQUESTSALT
      });
    }
  }

  if (typeof res?.data == 'object' && (res?.data instanceof Blob || res.data instanceof ArrayBuffer)) {
    const fileName = res.headers['content-disposition'];
    if (fileName) {
      res.data.fileName = fileName;
    }
    return Promise.resolve(res.data);
  }

  const code = ~~res.data?.errorcode || ~~res.data.code;

  // code === 0 为成功，直接返回，其余转错误处理
  return code ? Promise.reject(res) : res.data;
};

const retryCode = [201, 403, 401];

Axios.prototype.error = function (err) {
  if (err.response) {
    networkErrorToastShown = false;
  }
  if (err.code === 'ERR_NETWORK') {
    // 默认请求超时
    const response = {
      errorcode: 500,
      errordesc: i18n.global.t('common.tip.network_error')
    };

    let hideMessage = false;
    if (Object.prototype.hasOwnProperty.call(err, 'hideMessage')) {
      hideMessage = err.hideMessage;
    } else if (err.config) {
      hideMessage = err.config?.hideMessage || false;
    }
    // isLoginOut 限制多次请求只输出一次错误提示；networkErrorToastShown 限制 ERR_NETWORK 全周期只提醒一次直至网络恢复
    if (!isLoginOut && !hideMessage && !networkErrorToastShown) {
      networkErrorToastShown = true;
      debounceError(response);
    }
    return Promise.reject(response);
  }

  // 采集日志
  const { data, config } = err;
  err.config = err.config || {};

  if (err.config?.needTime) {
    const endEvent = new CustomEvent('httpLoadEnd', {
      detail: {
        requestId: err.config.__requestId,
        url: err.config.url,
        ...err.config,
        endTime: Date.now(),
        status: err.status,
        response: err.data,
        responseHeaders: JSON.stringify(err.request.getAllResponseHeaders())
      }
    });
    window.dispatchEvent(endEvent);
  } else if (err.config?.hideLogger && !err.config?.hideErrorLogger) {
    slsLogger.xhr({
      ...err.config,
      response: err.data,
      status: err.status,
      responseHeaders: JSON.stringify(err.request.getAllResponseHeaders())
    });
  }

  if ([201, 403, 404, 500].includes(+data?.code) && isElectron) {
    logger.error(
      JSON.stringify({
        url: config.url,
        response: config.data,
        header: {
          token: config.headers?.token || config.headers?.web_token,
          app: config.headers?.app,
          act: config.headers?.act,
          mod: config.headers?.mod,
          language: config.headers?.language,
          uid: config.headers?.uid
        },
        params: config.params
      })
    );
  }

  if (err instanceof axios.Cancel) {
    return Promise.reject({
      code: 'canceled'
    });
  }
  const code =
    ~~err?.response?.data?.code || ~~err?.response?.data?.errorcode || ~~err.data?.errorcode || ~~err.data?.code;

  if (retryCode.includes(code) && retryTokenCount > 1 && checkLogin()) {
    alertContext = { scene: ALERT_SCENE.HTTP_RETRY_LIMIT, config };
    alertFunc(code, i18n.global.t('common.tip.login_invalid'), true, '前往', () => {
      if (window.location.pathname !== '/login') {
        if (isElectron) return (location.href = `${CLIENT_URL}/login`);
        const redirectUrl = 'redirect_uri=' + window.location.pathname + window.location.search;
        if (isOverseasEnv()) {
          const redirectUrl =
            'redirect_uri=' + ENV_DATA.VITE_PROXY_OVERSEA + window.location.pathname + window.location.search;
          location.href = `${ENV_DATA.VITE_OVERSEAS_LOGIN_URL}?${redirectUrl}`;
        } else {
          router.push(`/login?${redirectUrl}`);
        }
      } else {
        window.location.reload();
      }
    });
    return Promise.reject();
  }

  // 默认请求超时
  let response = {
    errorcode: 500,
    errordesc: i18n.global.t('common.tip.network_error')
  };

  // 返回请求500错误
  if (err.response && err.response.status == 500) {
    response = {
      errorcode: 500,
      errordesc: i18n.global.t('common.tip.server_connection_failed')
    };
  }

  // 对返回值赋值
  Object.assign(response, err.data);

  // 处理errorcode 和 code
  response.errorcode = response.errorcode || response.code;
  response.errordesc = response.errordesc || response.msg || response.message;

  let hideMessage = false;
  if (Object.prototype.hasOwnProperty.call(err, 'hideMessage')) {
    hideMessage = err.hideMessage;
  } else if (err.config) {
    hideMessage = err.config.hideMessage;
  }

  // 牛币不足时返回的错误码
  const NBI_DIALOG_CODE = 70081;
  const isShowNbiDialog = code === NBI_DIALOG_CODE;

  // OAuth 授权页自行承接登录态失效：不弹全局确认框，保留交易参数直接返回独立登录页。
  const oauthLoginPath =
    window.location.pathname === MCP_OAUTH_AUTHORIZE_PATH
      ? MCP_OAUTH_LOGIN_PATH
      : window.location.pathname === THIRD_PARTY_OAUTH_AUTHORIZE_PATH
      ? THIRD_PARTY_OAUTH_LOGIN_PATH
      : window.location.pathname === MCP_OAUTH_BASE_AUTHORIZE_PATH
      ? MCP_OAUTH_BASE_LOGIN_PATH
      : '';
  // 绑定识别类接口需先拿到业务数据（如 bind_ticket）再由页面决定跳转，故支持单独关闭自动回登录页。
  if (oauthLoginPath && !err.config?.skipOauthLoginRedirect && LOGIN_INVALID_CODES.has(String(code))) {
    response.mcpOauthLoginHandled = true;
    if (isLoginOut) return Promise.reject(response);
    storage.remove(LOCAL_STORAGE_KEY.JM_AUTH_INFO);
    store.commit('setIsLogin', false);
    isLoginOut = true;
    const errorMessage = getLoginInvalidMessage(response);
    router
      .replace({ path: oauthLoginPath, query: router.currentRoute.value.query })
      .then(() => {
        ElMessage.error(errorMessage);
      })
      .finally(() => {
        isLoginOut = false;
      });
    return Promise.reject(response);
  }

  // isLoginOut 限制多次请求只输出一次错误提示
  if (isShowNbiDialog || (!isLoginOut && !hideMessage)) {
    alertContext = { scene: ALERT_SCENE.HTTP_ERROR, config };
    switch (code) {
      case 500:
      case 404:
      case 10086:
        alertFunc(code, response.errordesc, false, i18n.global.t('common.button.confirm'), () => {});
        break;
      case 201:
      case 20101:
      case 20102:
        store.commit('setIsLogin', false);

        alertFunc(code, getLoginInvalidMessage(response), true, i18n.global.t('common.button.confirm'), () => {
          if (window.location.pathname !== '/login') {
            // 判断本地是否存在用户登录信息 有则自动登录
            const cooperationInfo = storage.get(LOCAL_STORAGE_KEY.COOPERATION, true);
            if (cooperationInfo && cooperationInfo.userInfo && cooperationInfo.userChannel) {
              storage.clear();
              const hasQuery = window.location.search;
              const query = `${hasQuery ? '&' : '?'}userInfo=${encodeURIComponent(
                cooperationInfo.userInfo
              )}&userChannel=${cooperationInfo.userChannel}`;
              return (window.location.href = `${window.location.href}${query}`);
            }
            if (isElectron) return (location.href = CLIENT_URL + '/login');
            store.dispatch('logoutAction').then(() => {
              if (isOverseasEnv()) {
                location.href = ENV_DATA.VITE_OVERSEAS_LOGIN_URL;
              } else {
                router.push({
                  name: 'login'
                });
              }
            });
          } else {
            window.location.reload();
          }
        });
        break;
      case 1971:
        alertFunc(code, i18n.global.t('common.tip.account_not_exist'), true, null, () => {});
        break;
      case 1976:
        alertFunc(code, i18n.global.t('common.tip.account_disabled'), true, null, () => {});
        break;

      case 403:
        store.commit('setIsLogin', false);
        alertFunc(code, response.errordesc || response.msg || i18n.global.t('common.tip.login_invalid'), true);
        break;
      case 401:
        ElMessage.error(response.errordesc || response.msg || i18n.global.t('common.tip.no_permission'));
        break;
      // 验证码过期
      case 1001:
        // ElMessage.error('验证码已过期');
        break;
      // 验证码错误
      case 1002:
        //   ElMessage.error('验证码不正确');
        break;
      case 212:
        return invokeCaptcha(err);
      case 215: //异常用户校验
        return invokeSecurityAuth(err);
      case 213: //异常用户校验 - 人脸核身
        return invokeSecurityFace(err);
      case 1401:
        ElMessage.error(response.errordesc);
        break;
      case NBI_DIALOG_CODE:
        if (!err.config?.skipNbiDialog) {
          nbiConfirmDialog(
            {
              needPayNum: response.extra?.needPayNum,
              remainingNum: response.extra?.remainingNum,
              messageType: err.config?.nbiMessageType || 0
            },
            store
          );
        }
        return Promise.resolve({
          data: { isExhausted: true },
          extra: err.config?.skipNbiDialog ? response.extra : undefined
        });
      default:
        debounceError(response);
    }
  }

  return Promise.reject(response);
};

async function invokeCaptcha(err) {
  if (bolCaptcha) {
    try {
      await new Promise((resolve, reject) => {
        requestQueue.push({ resolve, reject });
      });
    } catch (e) {
      console.error(e);
      return Promise.reject(err.data);
    }
  } else {
    bolCaptcha = true;

    try {
      // 获取行为验证码票据
      const res = await new Axios().get(`${COMEINURL}/waf/captcha`);
      // 调用腾讯行为验证码
      const txRes = await txCaptcha({
        appid: decrypt(res.data.captchaAppId, 'FB1798D4015EE8ED')
      });
      // 完成验证解除异常
      const userInfo = getStorageAuthInfo(LOCAL_STORAGE_KEY.JM_AUTH_INFO) || {};
      await new Axios().get(
        `${COMEINURL}/waf/toAllowList`,
        {
          ticket: txRes.ticket,
          randStr: txRes.randstr,
          ...err.data.data
        },
        {
          headers: {
            token: userInfo.webtoken,
            Web_token: userInfo.webtoken
          }
        }
      );
      bolCaptcha = false;

      requestQueue.forEach(({ resolve }) => resolve());
      requestQueue = [];
    } catch (e) {
      console.log(e);
      bolCaptcha = false;
      console.error(e);
      requestQueue.forEach(({ reject }) => reject());
      requestQueue = [];

      err.data.msg = err.data.errordesc = i18n.global.t('common.tip.security_verification_failed');

      ElMessage({
        showClose: true,
        message: i18n.global.t('common.tip.security_verification_failed'),
        type: 'error'
      });

      return Promise.reject(err.data);
    }
  }

  return new Axios().request(err.config);
}

async function invokeSecurityAuth(err) {
  if (showSecurityCaptcha) {
    try {
      await new Promise((resolve, reject) => {
        requestQueue.push({ resolve, reject });
      });
    } catch (e) {
      console.error(e);
      err.data.msg = err.data.errordesc = i18n.global.t('common.tip.security_check_failed');

      return Promise.reject(err.data);
    }
  } else {
    showSecurityCaptcha = true;

    const data = err.data.data;
    store.dispatch('showSecurityAuthDialog', {
      securityAuth: true,
      securityAuthType: data && data.uuid,
      securityAuthContent: data && data.recipient,
      securityAuthCloseBtn: () => {
        showSecurityCaptcha = false;

        requestQueue.forEach(({ reject }) => reject());
        requestQueue = [];

        err.data.msg = err.data.errordesc = i18n.global.t('common.tip.security_check_failed');
        store.dispatch('tooltipsAction', {
          text: err.data.msg
        });
        return Promise.reject(err.data);
      },
      securityAuthSuccessBtn: () => {
        showSecurityCaptcha = false;

        requestQueue.forEach(({ resolve }) => resolve());
        requestQueue = [];

        store.dispatch('tooltipsAction', {
          text: i18n.global.t('common.tip.verification_success')
        });
      }
    });
  }

  return new Axios().request(err.config);
}

async function invokeSecurityFace(err) {
  if (showSecurityCaptcha) {
    try {
      await new Promise((resolve, reject) => {
        requestQueue.push({ resolve, reject });
      });
    } catch (e) {
      console.error(e);
      err.data.msg = err.data.errordesc = i18n.global.t('common.tip.security_check_failed');

      return Promise.reject(err.data);
    }
  } else {
    showSecurityCaptcha = true;

    const data = err.data.data;
    store.dispatch('showSecurityFaceDialog', {
      securityFace: true,
      securityFaceType: data && data.uuid,
      securityFaceCloseBtn: () => {
        showSecurityCaptcha = false;

        requestQueue.forEach(({ reject }) => reject());
        requestQueue = [];

        err.data.msg = err.data.errordesc = i18n.global.t('common.tip.security_check_failed');
        return Promise.reject(err.data);
      }
    });
  }

  return axios.request(err.config);
}

Axios.prototype.request = function (config) {
  return axios
    .request(config)
    .then((res) => this.success(res))
    .catch((err) => this.error(err));
};

Axios.prototype.post = function (url = '', body = {}, options = {}) {
  //初始化请求配置参数
  const axiosConfig = new AxiosConfig(url, body, options);
  if (axiosConfig.bodyType !== 'image' && axiosConfig.bodyType !== 'file') {
    axiosConfig.instance();
  }
  axiosConfig.method = 'post';
  axiosConfig.hideMessage = options.hideMessage;

  if (!axiosConfig.data) axiosConfig.data = {};

  switch (axiosConfig.bodyType) {
    case 'form':
      // 固定请求参数放在 body
      Object.assign(axiosConfig.data, axiosConfig.initUrl());

      axiosConfig.headers['Content-Type'] = 'application/x-www-form-urlencoded;charset=utf-8';
      axiosConfig.transformRequest = [
        (data) => {
          return Qs.stringify(data);
        }
      ];
      break;
    case 'image':
    case 'file':
      axiosConfig.headers['Content-Type'] = 'multipart/form-data';
      axiosConfig.timeout = 1800000; //超时时间为半小时

      const formData = new FormData();
      Object.keys(body).map((index) => {
        formData.append(index, body[index]);
      });
      axiosConfig.data = formData;
      break;
    default:
      // 默认json格式请求
      // 固定请求参数放在 headers
      Object.assign(axiosConfig.headers, axiosConfig.initUrl());
  }

  return this.request(axiosConfig);
};

Axios.prototype.put = function (url = '', body = {}, options = {}) {
  //初始化请求配置参数
  const axiosConfig = new AxiosConfig(url, body, options);
  if (axiosConfig.bodyType !== 'image' && axiosConfig.bodyType !== 'file') {
    axiosConfig.instance();
  }
  axiosConfig.method = 'put';

  switch (axiosConfig.bodyType) {
    case 'form':
      // 固定请求参数放在 body
      Object.assign(axiosConfig.data, axiosConfig.initUrl());

      axiosConfig.headers['Content-Type'] = 'application/x-www-form-urlencoded;charset=utf-8';
      axiosConfig.transformRequest = [
        (data) => {
          return Qs.stringify(data);
        }
      ];
      break;
    case 'image':
    case 'file':
      axiosConfig.headers['Content-Type'] = 'multipart/form-data';
      axiosConfig.timeout = 1800000; //超时时间为半小时

      const formData = new FormData();
      Object.keys(body).map((index) => {
        formData.append(index, body[index]);
      });
      axiosConfig.data = formData;
      break;
    default:
      // 默认json格式请求
      // 固定请求参数放在 headers
      Object.assign(axiosConfig.headers, axiosConfig.initUrl());
  }

  return this.request(axiosConfig);
};

Axios.prototype.get = function (url = '', body = {}, options = {}) {
  //初始化请求配置参数
  const axiosConfig = new AxiosConfig(url, body, options);
  axiosConfig.instance();
  axiosConfig.method = 'get';

  axiosConfig.params = body;

  Object.assign(axiosConfig.params, axiosConfig.initUrl());

  return this.request(axiosConfig);
};

function AxiosConfig(url = '', body = {}, options = {}) {
  // 发送请求的地址
  this.url = url;

  // 发送请求的数据
  this.data = body;

  // 被发送的自定义请求头
  this.headers = {};

  // 与请求一起发送的 URL 参数
  this.params = {};

  // 错误信息
  this.hideMessage = false;
  // 接口请求不强制上传日志
  this.hideLogger = true;
  // 接口请求失败不传日志
  this.hideErrorLogger = false;

  // 自定义的一些参数
  // bodyType: String     请求参数数据类型 支持 json / form / file / image 默认json
  // hideMessage: Boolean 请求失败是否显示错误信息
  // timeout              请求超时时间
  // retry: Number        请求失败重试次数
  // retryDelay: Number   请求失败重试延迟时间
  // retryCount: Number   请求失败已经重试次数
  // needTrack: Boolean   请求响应后 是否需要埋点
  // trackParams: Object  请求响应埋点参数
  Object.assign(this, options);
}

// 语言标准化函数,翻译给后端
export const normalizeLanguage = (lang, url) => {
  if (!lang) return 'zh-cn';
  // 后端接口白名单，命中的接口暂时不允许翻译成英文
  // 接口格式：App_Mod_Act
  const whiteUrlList = [
    // 'json_roadshow-list_preview',
    // 'json_roadshow_search-for-brm',
    // 'json_roadshow-list_playback-limited',
    // 'json_roadshow-list_no-playback',
    // 'json_dateschedule_list-by-day',
    // 'json_roadshow-list_theme',
    // 'json_roadshow_search',
    // 'json_roadshow-list_all',
    // 'json_brm-roadshow_company-roadshow'
  ];
  if (url && whiteUrlList.includes(url)) {
    return 'zh-cn';
  }

  const normalized = lang.toLowerCase();
  if (normalized === 'zh-cn' || normalized === 'zhcn' || normalized === 'zh_cn') {
    return 'zh-cn';
  } else if (normalized === 'zh-hant' || normalized === 'zhtc' || normalized === 'zh_tc') {
    return 'zh-hant';
  } else if (normalized === 'en' || normalized === 'en-us' || normalized === 'english') {
    return 'en';
  }
  return 'zh-cn'; // 默认返回简体中文
};

AxiosConfig.prototype.instance = function () {
  //默认设置token到请求头
  const userInfo = getStorageAuthInfo(LOCAL_STORAGE_KEY.JM_AUTH_INFO) || {};
  const cooperationInfo = storage.get(LOCAL_STORAGE_KEY.COOPERATION, true);
  // 用户ID (非必填)
  // 优先使用调用方显式传入的 uid，未传入时再回退到 storage，避免旧用户 uid 串号
  this.headers.uid = this.headers.uid || (userInfo ? userInfo.uid : '');
  // 会话令牌 (非必填)
  if (this.headers.hasOwnProperty('token') || this.headers.hasOwnProperty('webtoken')) {
    this.headers.token = this.headers.web_token = this.headers.token || this.headers.webtoken;
  } else {
    this.headers.token = this.headers.web_token = userInfo ? userInfo.webtoken || '' : '';
  }
  // 操作系统平台 ios android web
  this.headers.os = resolveOsHeader(isElectron ? 'pc-app' : 'brm');
  // os为web的扩展字段 pc端传值：pc , 手机端传值：mobile
  this.headers.c = 'pc';
  // os为web的扩展字段 web所处的环境 weixin qq comein minprogram
  this.headers.webenv = 'comein';
  // 手机操作系统
  this.headers.s = '';
  // 用户渠道 comein-app, comein-pc, comein-h5 , comein-miniprogram , comein-assist (非必填)
  this.headers.uc = cooperationInfo?.userChannel || 'comein-pc';
  // 浏览器
  this.headers.browse = window.navigator.appName;
  // user agent
  this.headers.ua = window.navigator.userAgent;
  // 外部展 示版本号(3.0.3)
  this.headers.b = isElectron ? '4.0.0800' : PORTVERSION;
  // 系统名称
  this.headers.sn = getSystemName();
  // 设备名称
  this.headers.dn = getDeviceName();
  // 不从store中获取语言，因为时序问题，因为store中的语言可能不是最新的
  // 语言language - 优先从 i18n 获取，其次从 cookie，再从 localStorage，最后从 navigator.language
  const language =
    i18n.global.locale.value ||
    getCookie(LOCAL_STORAGE_KEY.LANGUAGE) ||
    localStorage.getItem('lang') ||
    navigator.language;

  this.headers.language = normalizeLanguage(language, this.url);
  /** 上述参数中非必选的参数，在能拿到信息的情况下都需要传入接口。 */
  this.headers['Content-Type'] = 'application/json;charset=utf-8';

  // 错误信息
  this.hideMessage = false;
  // 接口请求不强制上传日志
  this.hideLogger = true;
  // 接口请求失败不传日志
  this.hideErrorLogger = false;
};

AxiosConfig.prototype.initUrl = function () {
  const arr = this.url.split('_');

  if (arr.length != 3) {
    return {};
  }
  // 方便本地调试
  if (ENV_DATA.VITE_APP_ENV.includes('overseas') && process.env.NODE_ENV === 'development') {
    this.url = '/api/comein/' + this.url;
  } else {
    this.url = `${COMEINURL}/${this.url}`;
  }

  const params = {
    app: arr[0],
    mod: arr[1],
    act: arr[2]
  };
  return params;
};

export default new Axios();
